import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MapPin, Sparkles, GraduationCap, Send, Heart, Building2, ShieldCheck, LogOut, User as UserIcon, Menu, Rocket, TrendingUp, Users, MessageSquare } from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";

export default function Layout({ children }) {
  const { user, logout, lang, setLanguage } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/map", label: t(lang, "nav_map"), icon: MapPin, testid: "nav-map" },
    { to: "/suggestions", label: t(lang, "nav_suggestions"), icon: Sparkles, testid: "nav-suggestions" },
    { to: "/roadmap", label: t(lang, "nav_roadmap"), icon: Rocket, testid: "nav-roadmap" },
    { to: "/internships", label: t(lang, "nav_internships"), icon: GraduationCap, testid: "nav-internships" },
    { to: "/applications", label: t(lang, "nav_applications"), icon: Send, testid: "nav-applications" },
    { to: "/favorites", label: t(lang, "nav_favorites"), icon: Heart, testid: "nav-favorites" },
  ];

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#1E3A8A] flex items-center justify-center">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-xl tracking-tight text-[#1E3A8A]">Proxijob</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map(({ to, label, icon: Icon, testid }) => (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  loc.pathname === to ? "bg-[#1E3A8A] text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <select
              data-testid="lang-select"
              value={lang}
              onChange={(e) => setLanguage(e.target.value)}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-full border border-slate-300 hover:bg-slate-100 bg-white transition-colors cursor-pointer"
              aria-label="Language"
            >
              <option value="fr">🇫🇷 FR</option>
              <option value="en">🇬🇧 EN</option>
              <option value="es">🇪🇸 ES</option>
              <option value="de">🇩🇪 DE</option>
            </select>

            {user ? (
              <>
                <NotificationBell />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="flex items-center gap-2 rounded-full p-1 hover:bg-slate-100 transition-colors">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-[#1E3A8A] text-white text-xs">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile"><UserIcon className="w-4 h-4 mr-2" /> {t(lang, "nav_profile")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/company")} data-testid="menu-company"><Building2 className="w-4 h-4 mr-2" /> {t(lang, "nav_company")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/pricing")} data-testid="menu-pricing"><Sparkles className="w-4 h-4 mr-2" /> {t(lang, "nav_pricing")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/messages")} data-testid="menu-messages"><MessageSquare className="w-4 h-4 mr-2" /> {lang === "fr" ? "Messagerie" : "Messages"}</DropdownMenuItem>
                  {user?.user_type === "admin" && (
                    <DropdownMenuItem onClick={() => nav("/admin/claims")} data-testid="menu-admin-claims"><ShieldCheck className="w-4 h-4 mr-2" /> {lang === "fr" ? "Revendications" : "Claims"}</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => nav("/analytics")} data-testid="menu-analytics"><TrendingUp className="w-4 h-4 mr-2" /> Analytics</DropdownMenuItem>
                  {user.user_type === "teacher" && (
                    <DropdownMenuItem onClick={() => nav("/teacher")} data-testid="menu-teacher"><Users className="w-4 h-4 mr-2" /> {lang === "fr" ? "Espace enseignant" : "Teacher space"}</DropdownMenuItem>
                  )}
                  {user.user_type === "admin" && (
                    <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin"><ShieldCheck className="w-4 h-4 mr-2" /> {t(lang, "nav_admin")}</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" /> {t(lang, "logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" data-testid="header-login" onClick={() => nav("/login")}>{t(lang, "cta_login")}</Button>
                <Button data-testid="header-signup" onClick={() => nav("/register")} className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full">{t(lang, "cta_signup")}</Button>
              </>
            )}

            <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-2 flex flex-col gap-1">
            {links.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-sm">
                <Icon className="w-4 h-4" />{label}
              </Link>
            ))}
          </div>
        )}
      </header>
      <main>{children}</main>
      <footer data-testid="app-footer" className="border-t border-slate-200 bg-white/50 backdrop-blur-sm mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-[#1E3A8A] flex items-center justify-center">
              <MapPin className="w-3 h-3 text-white" />
            </div>
            <span>© {new Date().getFullYear()} Proxijob · {lang === "fr" ? "Le marché caché de l'emploi" : "The hidden job market"}</span>
          </div>
          <nav className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#1E3A8A]">{lang === "fr" ? "Confidentialité" : "Privacy"}</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-[#1E3A8A]">{lang === "fr" ? "CGU" : "Terms"}</Link>
            <a href="mailto:hello@proxijob.app" data-testid="footer-contact" className="hover:text-[#1E3A8A]">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
